import { IconButton, SxProps, TableCell, Theme } from "@mui/material";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { FichaImagemAssociacao } from "redux/features/imagemReducer";
import { theme } from "theme";
import { isEmpty } from "utils/util";

export type TMiniImagemProps = {
    uuidProduto: string;
    imagens: FichaImagemAssociacao[];
    descritivo: string;
    style?: SxProps<Theme> | undefined;
    onOpenImage: (uuidProduto: string) => void;
};

const buildThumb = (url: string, size = 60, fmt = "webp") => {
    try {
        const u = new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost");
        u.searchParams.set("w", String(size));
        u.searchParams.set("h", String(size));
        u.searchParams.set("fit", "contain");
        u.searchParams.set("q", "60");
        u.searchParams.set("fm", fmt);
        return u.toString();
    } catch {
        return url;
    }
};

function useInViewport<T extends HTMLElement>() {
    const ref = useRef<T | null>(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!ref.current) return;
        const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: "200px" });
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return [ref, visible] as const;
}

const MiniImagem = (props: TMiniImagemProps) => {
    const hasAssociacoes = !isEmpty(props.imagens);
    const firstAssoc = hasAssociacoes ? props.imagens[0] : undefined;
    const firstImagem = firstAssoc?.imagens?.[0];
    const [cellRef, visible] = useInViewport<HTMLTableCellElement>();

    const thumbUrl = useMemo(() => {
        const url = firstImagem?.url;
        if (!url) return "";
        return buildThumb(url, 60, "webp");
    }, [firstImagem?.url]);

    return (
        <TableCell ref={cellRef} align="right" sx={{ ...props.style, textAlign: "center" }}>
            <IconButton onClick={() => props.onOpenImage(props.uuidProduto)}>
                {firstImagem && visible ? (
                    <img
                        src={thumbUrl}
                        alt={firstImagem.infoAdicional ?? props.descritivo ?? "Imagem do produto"}
                        width={30}
                        height={30}
                        loading="lazy"
                        decoding="async"
                        style={{ objectFit: "contain", display: "inline-block", aspectRatio: "1 / 1" }}
                    />
                ) : (
                    <i
                        className="fa-solid fa-camera"
                        style={{ color: theme.palette.text.disabled, width: 30, height: 30 }}
                        aria-label="Sem imagem"
                    />
                )}
            </IconButton>
        </TableCell>
    );
};

export default memo(
    MiniImagem,
    (a, b) =>
        a.uuidProduto === b.uuidProduto &&
        a.descritivo === b.descritivo &&
        a.style === b.style &&
        (a.imagens?.[0]?.imagens?.[0]?.url ?? "") === (b.imagens?.[0]?.imagens?.[0]?.url ?? "")
);
